import {
  ChevronDownIcon,
  ChevronUpIcon
} from "@heroicons/react/24/outline";
import { useApplicationModals } from "app/common/hooks";
import {
  BadgeButton,
  IconButton,
  ServerSidePaginationButtons,
} from "app/components/atoms/button";
import { SearchInput } from "app/components/atoms/input";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { ModalMolecule } from "app/components/molecules/modal";
import { usePaginatedCollector } from "app/integration/common/hooks";
import React, { useEffect, useState } from "react";
import { isUniqueBarcode } from "app/common/product";

function ChooseProduct({ onSuccess = () => { } }) {
  const { showStates, getPayloads, closeModal } = useApplicationModals();
  const payload = getPayloads("product.choose");

  const handleClose = () => {
    closeModal("product.choose");
  };
  const handleSuccess = (data) => {
    closeModal("product.choose");
    // console.log("customer", data)
    onSuccess(data);
  };

  const [openRow, setOpenRow] = useState(null);

  const toggleRow = (id) => {
    setOpenRow(openRow === id ? null : id);
  };

  const [params, setParams] = useState({
    search: "",
    product_search: "",
    // Filter pelacakan stok. Nilainya STRING — "" (tanpa filter), "true", "false" —
    // dan itu BUKAN pilihan gaya.
    //
    // `CrudService.getAll` meneruskan query param jadi klausa where dengan syarat
    // `data[query] != ""`. Di JavaScript `false == ""` bernilai TRUE, jadi boolean
    // `false` akan DIBUANG diam-diam dan filternya tidak berlaku — daftar tetap
    // menampilkan semua produk tanpa tanda apa pun bahwa filternya diabaikan.
    // Sudah diuji: string "false" menyaring benar, boolean false tidak.
    is_unique_barcode: "",
    status: null,
    customer_id: null,
    page: 1,
    limit: 15,
  });

  useEffect(() => {
    collect(params);
  }, [params]);

  const [searchValue, setSearchValue] = useState("");

  const [
    products,
    setProducts,
    productsLoader,
    collect,
    productPaginationProps,
  ] = usePaginatedCollector({ modelName: "product", params: params });

  const filteredProducts = searchValue
    ? products?.filter((product) => {
      const productName = `${product.name || ""}`.toLowerCase();
      const productCode = product.code?.toLowerCase() || "";

      return (
        productName.includes(searchValue.toLowerCase()) ||
        productCode.includes(searchValue.toLowerCase())
      );
    })
    : products;

  return (
    <ModalMolecule
      show={showStates["product.choose"]}
      title="Find Product"
      onClose={handleClose}
      size="lg"
    >
      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <SearchInput testId="modal-choose-product-search-input"
              placeholder="Cari Produk"
              type="text"
              value={params.product_search}
              // onChange={handleQueryChange}
              onChange={(e) =>
                setParams({ ...params, product_search: e.target.value })
              }
            />
            <select data-testid="modal-choose-product-tracking-select"
              className="ml-2 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={params.is_unique_barcode}
              onChange={(e) =>
                setParams({
                  ...params,
                  is_unique_barcode: e.target.value,
                  page: 1, // reset halaman: hasil filter jumlahnya berbeda
                })
              }
            >
              <option value="">Semua Pelacakan</option>
              <option value="true">Per Pcs (barcode unik)</option>
              <option value="false">Per Qty (tanpa barcode unik)</option>
            </select>
          </Table.SearchBarWrapper>
        </Table.UtilitiesWrapper>
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Kode</Table.Heading>
              <Table.Heading>Product</Table.Heading>
              <Table.Heading>Stock</Table.Heading>
              <Table.Heading>Status</Table.Heading>
              <Table.Heading>Pelacakan</Table.Heading>
              <Table.Heading>Harga</Table.Heading>
              <Table.Heading></Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {filteredProducts?.map((product, key) => (
              <React.Fragment key={key}>
                <Table.BodyRow>
                  <Table.Cell>
                    <p>{product?.code}</p>
                    <Typography.Date
                      format="dd MMM yyyy"
                      size="xs"
                      date={product.createdAt}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <p>{product?.name}</p>
                    <p className="text-gray-500 font-light text-sm">
                      {product?.code}
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                  </Table.Cell>
                  <Table.Cell>
                  </Table.Cell>
                  <Table.Cell>
                    {/* `!== false` supaya produk yang kolomnya belum terbaca tampil
                        "Per Pcs", sama dengan default kolomnya. */}
                    {isUniqueBarcode(product?.is_unique_barcode) ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 whitespace-nowrap">
                        Per Pcs
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 whitespace-nowrap">
                        Per Qty
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Typography.Currency number={product?.price} />
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <div className="flex">
                        <IconButton testId="modal-choose-product-chevron-up-button"
                          color="green"
                          icon={
                            openRow === product.id
                              ? ChevronUpIcon
                              : ChevronDownIcon
                          }
                          onClick={(e) => {
                            toggleRow(product.id);
                          }}
                        />
                      </div>
                    </div>
                  </Table.Cell>
                </Table.BodyRow>
                {/* Baris tambahan (detail) */}
                {openRow === product.id &&
                  product?.ProductVariants?.map((variant, key2) => {
                    return (
                      <Table.BodyRow key={key2}>
                        <Table.Cell></Table.Cell>
                        <Table.Cell>
                          <p className="text-left">
                            {variant?.color?.name}, {variant?.size?.name}
                          </p>
                        </Table.Cell>
                        <Table.Cell>
                          <Typography.Currency number={variant?.qty} />
                        </Table.Cell>
                        <Table.Cell>
                          {variant?.qty === 0 ? (
                            <BadgeButton testId="modal-choose-product-habis-button" color="red" size="sm">Habis</BadgeButton>
                          ) : variant?.qty < 5 ? (
                            <BadgeButton testId="modal-choose-product-hampir-habis-button" color="yellow" size="sm">Hampir Habis</BadgeButton>
                          ) : (
                            <BadgeButton testId="modal-choose-product-tersedia-button" color="green" size="sm">Tersedia</BadgeButton>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Typography.Currency number={variant?.price} />
                        </Table.Cell>
                        <Table.Cell>
                          <BadgeButton testId="modal-choose-product-select-button"
                            color="green"
                            onClick={(e) => {
                              handleSuccess({ product, variant });
                            }}
                          >
                            Select
                          </BadgeButton>
                        </Table.Cell>
                      </Table.BodyRow>
                    );
                  })}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table.Wrapper>
        <Table.PaginationWrapper>
          <ServerSidePaginationButtons
            paginationProps={productPaginationProps}
            params={params}
            setParams={setParams}
          />
        </Table.PaginationWrapper>
      </Table.Container>
    </ModalMolecule>
  );
}

export default ChooseProduct;
